import React from "react";
import { Phone, Mail, MapPin } from "lucide-react";
import { useLanguage } from "../context/LanguageContext";
import { useLocation, useNavigate } from "react-router-dom";

export default function Footer() {
  const footerLogo = "/logo-light.svg";
  const { t, language } = useLanguage();
  const location = useLocation();
  const navigate = useNavigate();

  const siteLinks =
    language === "en"
      ? [
          { label: "Contact us", href: "/#contact", id: "contact" },
          { label: "Reglazing services", href: "/#service", id: "service" },
          { label: "Our services", href: "/#services", id: "services" },
          { label: "FAQ", href: "/#faq", id: "faq" },
          { label: "Try the AI preview free", href: "/ai-preview" },
        ]
      : [
          { label: "Nous joindre", href: "/#contact", id: "contact" },
          { label: "Services de réémaillage", href: "/#service", id: "service" },
          { label: "Nos Services", href: "/#services", id: "services" },
          { label: "FAQ", href: "/#faq", id: "faq" },
          { label: "Essayer l’aperçu IA gratuitement", href: "/ai-preview" },
        ];

  const handleNavLink = (link, e) => {
    if (e) e.preventDefault();
    if (!link.id) {
      navigate(link.href);
      if (link.href === "/ai-preview") {
        window.setTimeout(() => {
          window.scrollTo({ top: 0, left: 0, behavior: "auto" });
        }, 0);
      }
      return;
    }

    if (location.pathname === "/") {
      const el = document.getElementById(link.id);
      if (el) {
        el.scrollIntoView({ behavior: "smooth" });
        window.history.replaceState(null, "", link.href);
      }
      return;
    }
    navigate(link.href);
  };

  return (
    <footer className="text-white bg-gradient-to-b from-[#081427] to-[#050d1a] dark:from-[#141617] dark:to-[#0f1112] transition-colors duration-300">
      <div className="max-w-7xl mx-auto px-8 sm:px-10 lg:px-16 py-16 lg:py-20">
        <div className="grid grid-cols-1 gap-12 lg:grid-cols-12 lg:gap-12 items-start text-left">
          <div className="lg:col-span-5 flex flex-col items-start">
            <img
              src={footerLogo}
              alt="Éco-Bain"
              width="157"
              height="157"
              loading="lazy"
              className="h-10 sm:h-12 lg:h-20 w-auto object-contain"
            />
            <p className="text-slate-300 max-w-sm leading-relaxed mt-5">
              {t("footer.tagline")}
            </p>
          </div>

          <div className="lg:col-span-4 lg:pl-10 lg:border-l lg:border-white/10">
            <h3 className="text-lg font-semibold mb-4">{t("footer.serviceArea")}</h3>
            <div className="flex items-start gap-3 text-slate-300">
              <MapPin className="w-5 h-5 text-green-400 mt-1 shrink-0" />
              <p className="leading-relaxed">{t("footer.serviceAreaDesc")}</p>
            </div>
          </div>

          <div className="lg:col-span-3 lg:pl-10 lg:border-l lg:border-white/10">
            <h3 className="text-lg font-semibold mb-4">{t("footer.contact")}</h3>

            <a
              href={`tel:${t("nav.phone")}`}
              className="flex items-center gap-3 mb-3 text-slate-300 hover:text-white transition"
            >
              <Phone className="w-5 h-5 text-green-400 shrink-0" />
              <span>{t("nav.phone")}</span>
            </a>

            <a
              href="mailto:info@eco-bain.ca"
              className="flex items-center gap-3 mb-4 text-slate-300 hover:text-white transition"
            >
              <Mail className="w-5 h-5 text-green-400 shrink-0" />
              <span>info@eco-bain.ca</span>
            </a>

            <p className="text-slate-400 text-sm leading-relaxed">
              <span className="font-medium text-slate-300">{t("footer.hours")}</span>
              <br />
              {t("footer.hoursDesc")}
            </p>
          </div>
        </div>

        <div className="mt-14 border-t border-white/10 pt-7 text-center text-slate-400 text-sm">
          <div className="mb-3">
            <nav className="flex flex-wrap items-center justify-center gap-x-6 gap-y-3 mb-3">
              {siteLinks.map((link) => (
                <a
                  key={link.href}
                  href={link.href}
                  onClick={(e) => handleNavLink(link, e)}
                  className="hover:text-white transition"
                >
                  {link.label}
                </a>
              ))}
            </nav>
          </div>

          {t("footer.copyright")}
        </div>
      </div>
    </footer>
  );
}
